import toast from 'react-hot-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { client } from '@/api/client'
import type { ServerApiResponse } from '../../domain/types'

type UpdateServerNameParams = {
  id: string
  name: string
}

type MutationContext = {
  previousServers: ServerApiResponse[] | undefined
}

type ApiError = {
  response: {
    status: number
    ok: boolean
  }
  error?: { error: string }
}

export function useUpdateServerName() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, name }: UpdateServerNameParams) => {
      const result = await client.PATCH('/v1/nodes/{node_id}', {
        params: { path: { node_id: id } },
        body: { name: name.trim() },
      })

      if (!result.response.ok) {
        throw result
      }

      return { id, name }
    },

    onMutate: async ({ id, name }: UpdateServerNameParams) => {
      await queryClient.cancelQueries({ queryKey: ['servers'] })

      const previousServers = queryClient.getQueryData<ServerApiResponse[]>(['servers'])

      // Update all server queries optimistically
      queryClient.setQueryData<ServerApiResponse[]>(['servers'], (oldData) => {
        if (!oldData) return oldData

        return oldData.map((server) =>
          server.id === id ? { ...server, name } : server,
        )
      })

      return { previousServers }
    },

    onError: (error: ApiError, _, context: MutationContext | undefined) => {
      queryClient.setQueryData<ServerApiResponse[]>(['servers'], context?.previousServers)

      const errorMessage = error.error?.error || 'Server name could not be updated'
      toast.error(errorMessage)
    },

    onSuccess: () => {
      toast.success('Server name was updated successfully')
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['servers'] })
    },
  })
}